import {customElement, property, query, state} from "lit/decorators.js";
import {html, LitElement, type TemplateResult} from "lit";
import Styles from "./careergpt.lit.scss";
import {SessionStorage} from "../../common/clientStorage";
import {resolve} from "../../container";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {Resolution} from "../../common/resolution";
import {PageScrollbar} from "../../common/pageScrollbar";
import {BOOTSTRAP} from "../../common/resolutionConstants";
import {ManagingResources} from "../../common/lifetime";
import {Dictionary} from "../../page/elements/dictionary";
import {VisualViewportListener} from "../../common/visualViewportListener";
import {Page} from "../../common/page";
import {ScrollService} from "../../common/scroll";
import {Sanitizer} from "../../common/sanitizer";
import {generateUuid} from "../../common/crypto";
import {type GptChatApp, GptChatAppFactory} from "./gptChatApp";
import {ClickTracking} from "../../tracking/clickTracking";
import type {Nullable} from "../../bootstrap/common/types";
import {classMap} from "lit/directives/class-map.js";
import {waitTime} from "../../common/utils/promises";
import {GLOBAL} from "../../common/globals";
import {DeviceDetectorService} from "../../common/device";
import {KEYS} from "../../common/utils/events";

export const CHARACTER_LIMIT = 1000; // should be the same as in the backend
const ATTENTION_POPOVER_NOTICED_KEY = "career-gpt-attention-popover-noticed";

export type Message = {
    content: string;
    role: Role;
}

export enum Role {
    ASSISTANT = "assistant",
    USER = "user",
}

const ATTENTION_POPOVER_OPEN_TRACKING_LABEL = "career-gpt-popover-open";
const ATTENTION_POPOVER_CLOSE_TRACKING_LABEL = "career-gpt-popover-close";
const TOGGLE_OPEN_TRACKING_LABEL = "career-gpt-toggle-open";
const TOGGLE_CLOSE_TRACKING_LABEL = "career-gpt-toggle-close";
const CHAT_CLOSE_TRACKING_LABEL = "career-gpt-chat-window-close";
const SEND_USER_MESSAGE_TRACKING_LABEL = "career-gpt-send-user-message";

@customElement("eop-career-gpt")
export class EopCareerGpt extends ManagingResources(LitElement) {

    public static readonly styles = Styles;

    @property({attribute: "config-id"})
    public configId: string;
    @property({attribute: "image-src"})
    private imageSrc: string;
    @property({attribute: "image-src-dark"})
    private imageSrcDark: string;
    @property({attribute: "image-alt"})
    private imageAlt: string;
    @property({attribute: "bot-name"})
    private botName: string;
    @property({attribute: "welcome-message"})
    private welcomeMessage: string;
    @property({attribute: "show-id", type: Boolean})
    private showId: boolean;


    @property({type: Boolean, reflect: true, attribute: "attention-popover-noticed"})
    public attentionPopoverNoticed: boolean = false;
    @property({type: Boolean, reflect: true})
    public open: boolean = false;
    @property({type: Boolean, reflect: true})
    public floatingSideElementsActive: boolean = false;
    @state()
    private messages: Message[] = [];
    @state()
    private gptIsTyping: boolean = false;
    @state()
    private isChatSendButtonDisabled: boolean = true;
    @state()
    private characterLimitExceeded: boolean = false;
    @query(".chat-input")
    private textArea: HTMLTextAreaElement;
    @query(".chat-message:last-child")
    private lastChatMessage: HTMLElement;
    @query(".chat-toggle")
    private chatToggle: HTMLButtonElement;


    private dictionary: Dictionary;
    private chatTracingId: string;
    private emptyInput: boolean = true;

    private gptChatApp: GptChatApp;

    public constructor(
        private gptChatAppFactory: GptChatAppFactory = resolve(GptChatAppFactory),
        private pageScrollbar: PageScrollbar = resolve(PageScrollbar),
        private resolution: Resolution = resolve(Resolution),
        private scrollService: ScrollService = resolve(ScrollService),
        private visualViewportListener: VisualViewportListener = resolve(VisualViewportListener),
        private page: Page = resolve(Page),
        private sessionStorage: SessionStorage = resolve(SessionStorage),
        private sanitizer: Sanitizer = resolve(Sanitizer),
        private clickTracking: ClickTracking = resolve(ClickTracking),
        private device: DeviceDetectorService = resolve(DeviceDetectorService)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.setVisualViewportHeight();
        this.visualViewportListener.onResize(() => this.setVisualViewportHeight());
        this.visualViewportListener.onResize(() => this.scrollChatIntoView());

        this.dictionary = Dictionary.of(this);
        this.chatTracingId = generateUuid();

        this.gptChatApp = this.gptChatAppFactory.create(this.configId, this.chatTracingId, this.dictionary);
        this.gptChatApp.onNewMessages((messages) => this.receiveNewMessages(messages));
        this.gptChatApp.init(this.welcomeMessage);

        this.checkIfAttentionPopoverAlreadyNoticed();
    }

    private receiveNewMessages(messages: Message[]): void {
        this.messages = messages;
    }

    public render(): TemplateResult {
        return html`
            ${this.renderAttentionPopover()}
            ${this.renderChatToggle()}
            <div class="chat-window">
                ${this.renderHeader()}
                ${this.renderBody()}
                ${this.renderFooter()}
                ${this.renderTracingId()}
            </div>
        `;
    }

    public updated(): void {
        void this.scrollChatIntoView();
    }

    private async scrollChatIntoView(): Promise<void> {
        if (this.open) {
            // needed for iOS to "pin" the chat window to the bottom
            if (this.device.isIOS()) {
                void this.scrollService.scrollToTop(0);
                await waitTime(0);
            }
            void this.scrollService.scrollToElement(this.lastChatMessage);
        }
    }

    private renderAttentionPopover(): Nullable<TemplateResult> {
        if (this.attentionPopoverNoticed) {
            return null;
        }
        return html`
            <span class="attention-popover-container">
                <div class="attention-popover" @click=${this.handleAttentionPopoverClick}>
                    <button type="button"
                            class="close-button"
                            aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")}
                            @click=${this.handleAttentionPopoverCloseClick}
                    ></button>
                    <span>${this.dictionary.translate("CAREER_GPT_ATTENTION_POPOVER_MESSAGE")}</span>
                </div>
            </span>
        `;
    }

    private renderChatToggle(): TemplateResult {
        return html`
            <button
                    class="chat-toggle"
                    type="button"
                    aria-expanded=${this.open}
                    aria-label="career chat toggle"
                    @click=${this.toggleChatWindow}
            ></button>
        `;
    }

    private renderHeader(): TemplateResult {
        return html`
            <div class="chat-header">
                <div class="chat-avatar">
                    <eop-responsive-image image-src=${this.imageSrc} image-src-dark=${this.imageSrcDark} image-alt=${this.imageAlt}></eop-responsive-image>
                </div>
                <div class="chat-header-title">${this.botName}</div>
                <button type="button"
                        class="close-button"
                        aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")}
                        @click=${this.directlyCloseChatWindow}
                ></button>
            </div>
        `;
    }

    private renderBody(): Nullable<TemplateResult> {
        // TODO: find a clever way to define the scrollable container on mobile and desktop
        const isMobile = this.resolution.upTo(BOOTSTRAP.XS);
        const containerClasses = classMap({
            "scroll-container": true,
            "vertically-scrollable-area": isMobile
        });
        const chatMessagesClasses = classMap({
            "chat-messages": true,
            "vertically-scrollable-area": !isMobile
        });
        // TODO: end of todo
        return html`
            <div class=${containerClasses}>
                <div class="chat-body">
                    <div class=${chatMessagesClasses}>
                        ${this.renderMessages()}
                    </div>
                    <div class="typing-indicator" ?hidden=${!this.gptIsTyping}>
                        ${this.dictionary.translate("CAREER_GPT_TYPING_INDICATOR")}
                        <div class="dots-flashing">
                            <div class="dot"></div>
                        </div>
                    </div>
                    <div class="chat-input-warning" ?hidewarning=${!this.characterLimitExceeded}>
                        ${this.dictionary.translate("CAREER_GPT_INPUT_WARNING")}
                    </div>
                </div>
            </div>
        `;
    }

    private renderMessages(): TemplateResult[] {
        return this.messages.map((message) =>
            html`
                <div class="chat-message ${message.role}">
                    <div class="chat-message-content">${unsafeHTML(this.sanitizer.sanitizeHTML(message.content))}</div>
                </div>
            `);
    }

    private renderFooter(): Nullable<TemplateResult> {
        return html`
            <div class="chat-footer">
                <textarea id="career-gpt-chat-input"
                          placeholder=${this.dictionary.translate("CAREER_GPT_INPUT_PLACEHOLDER")}
                          title="text-input" class="chat-input"
                          @keydown=${this.keydownOnInput}
                          @input=${this.updateChatInputState}
                ></textarea>
                <button class="chat-send-button" type="button" ?disabled=${this.isChatSendButtonDisabled} @click=${this.sendInput}></button>
            </div>
        `;
    }

    private renderTracingId(): Nullable<TemplateResult> {
        if (!this.showId) {
            return null;
        }
        return html`
            <span class="chat-tracing">
                <span class="chat-tracing-id">Chat-ID zur Nachverfolgung: ${this.chatTracingId}</span>
                <span class="copy-icon" @click=${this.copyChatTracingId}></span>
            </span>
        `;
    }

    private copyChatTracingId(): void {
        void GLOBAL.navigator().clipboard.writeText(this.chatTracingId);
    }

    private handleAttentionPopoverClick(event: Event): void {
        event.stopPropagation();
        this.clickTracking.handleButtonInteract(event, ATTENTION_POPOVER_OPEN_TRACKING_LABEL);
        this.openChatWindow();
    }

    private handleAttentionPopoverCloseClick(event: Event): void {
        event.stopPropagation();
        this.clickTracking.handleButtonInteract(event, ATTENTION_POPOVER_CLOSE_TRACKING_LABEL);
        this.closeAttentionPopover(event);
    }

    private closeAttentionPopover(event?: Event): void {
        this.sessionStorage.put(ATTENTION_POPOVER_NOTICED_KEY, true);
        this.attentionPopoverNoticed = true;
        event?.stopPropagation();
        this.requestUpdate();
        void this.updateComplete.then(() => this.chatToggle.focus());
    }

    private checkIfAttentionPopoverAlreadyNoticed(): boolean {
        this.attentionPopoverNoticed = this.sessionStorage.fetch<boolean>(ATTENTION_POPOVER_NOTICED_KEY) ?? false;
        return this.attentionPopoverNoticed;
    }

    private toggleChatWindow(event: Event): void {
        event.stopPropagation();
        this.clickTracking.handleButtonInteract(event, this.open ? TOGGLE_CLOSE_TRACKING_LABEL : TOGGLE_OPEN_TRACKING_LABEL);

        if (this.open) {
            this.closeChatWindow();
        } else {
            this.openChatWindow();
        }
    }

    private directlyCloseChatWindow(event: Event): void {
        event.stopPropagation();
        this.clickTracking.handleButtonInteract(event, CHAT_CLOSE_TRACKING_LABEL);
        this.closeChatWindow();
    }

    private openChatWindow(): void {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            this.pageScrollbar.disablePageScrollability();
        }
        this.closeAttentionPopover();
        this.open = true;
        this.floatingSideElementsActive = this.open;
        void this.updateComplete.then(() => this.textArea.focus());
    }

    private closeChatWindow(): void {
        this.open = false;
        this.floatingSideElementsActive = this.open;
        this.pageScrollbar.enablePageScrollability();
        this.chatToggle.focus();
    }

    private updateChatInputState(): void {
        this.characterLimitExceeded = this.textArea?.value.length > CHARACTER_LIMIT;
        this.emptyInput = this.textArea?.value.trim().length === 0;
        this.isChatSendButtonDisabled = this.gptIsTyping || this.emptyInput || this.characterLimitExceeded;
    }

    private async keydownOnInput(event: KeyboardEvent): Promise<void> {
        switch (event.key) {
            case KEYS.ESCAPE:
                this.directlyCloseChatWindow(event);
                break;
            case KEYS.ENTER:
                await this.sendInput(event);
                break;
            default:
                break;
        }
    }

    private async sendInput(event: Event): Promise<void> {
        if (this.emptyInput) {
            return;
        }

        event.stopPropagation();
        this.clickTracking.handleButtonInteract(event, SEND_USER_MESSAGE_TRACKING_LABEL);

        const userInput = this.textArea.value;
        this.textArea.value = "";
        this.emptyInput = true;

        this.waitForGpt(true);
        await this.gptChatApp.chat(userInput);

        this.waitForGpt(false);
        this.textArea.focus();
    }

    private waitForGpt(b: boolean): void {
        this.gptIsTyping = b;
        this.textArea.disabled = b;
        this.updateChatInputState();
    }

    private setVisualViewportHeight(): void {
        this.style.setProperty("--visual-viewport-height", `${this.page.visualViewportHeight()}px`);
    }
}
